/**
 * VoteControls - The Intelligent Standard
 *
 * Voting buttons with clear hierarchy and brand-appropriate styling.
 */

export type VoteChoice = 'a' | 'b' | 'tie' | 'both_bad';

export interface VoteControlsProps {
  onVote: (choice: VoteChoice) => void | Promise<void>;
  disabled?: boolean;
  currentVote?: VoteChoice | null;
  autoAdvance?: boolean;
  voting?: boolean;
}

export function VoteControls({
  onVote,
  disabled = false,
  currentVote = null,
  autoAdvance = true,
  voting = false,
}: VoteControlsProps) {
  const handleVote = async (choice: VoteChoice) => {
    if (disabled || voting) return;
    await onVote(choice);
  };

  const isDisabled = disabled || voting;

  const getButtonStyle = (choice: VoteChoice): React.CSSProperties => {
    const isSelected = currentVote === choice && !isDisabled;

    // Primary choices (Model A, Model B) get more visual weight
    const isPrimary = choice === 'a' || choice === 'b';

    if (isSelected) {
      return {
        background: 'var(--color-meridian)',
        color: 'var(--color-white)',
        border: '2px solid var(--color-meridian)',
      };
    }

    return {
      background: 'var(--color-white)',
      color: isPrimary ? 'var(--color-ink)' : 'var(--color-slate)',
      border: '2px solid var(--color-cream-dark)',
    };
  };

  const showSelected = (choice: VoteChoice) =>
    currentVote === choice && !isDisabled;

  return (
    <div className="flex justify-center">
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => handleVote('a')}
          disabled={isDisabled}
          className="btn-arena px-6 py-1 rounded-lg text-[0.9375rem] font-medium"
          style={{
            ...getButtonStyle('a'),
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            opacity: isDisabled ? 0.5 : 1,
          }}
        >
          {showSelected('a') ? 'Model A' : 'Model A is better'}
        </button>

        <button
          onClick={() => handleVote('tie')}
          disabled={isDisabled}
          className="btn-arena px-6 py-1 rounded-lg text-[0.9375rem] font-medium"
          style={{
            ...getButtonStyle('tie'),
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            opacity: isDisabled ? 0.5 : 1,
          }}
        >
          {showSelected('tie') ? 'Tie' : 'Tie'}
        </button>

        <button
          onClick={() => handleVote('both_bad')}
          disabled={isDisabled}
          className="btn-arena px-6 py-1 rounded-lg text-[0.9375rem] font-medium"
          style={{
            ...getButtonStyle('both_bad'),
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            opacity: isDisabled ? 0.5 : 1,
          }}
        >
          {showSelected('both_bad') ? 'Both Bad' : 'Both are bad'}
        </button>

        <button
          onClick={() => handleVote('b')}
          disabled={isDisabled}
          className="btn-arena px-6 py-1 rounded-lg text-[0.9375rem] font-medium"
          style={{
            ...getButtonStyle('b'),
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            opacity: isDisabled ? 0.5 : 1,
          }}
        >
          {showSelected('b') ? 'Model B' : 'Model B is better'}
        </button>
      </div>
    </div>
  );
}
